import { useParams, useNavigate } from 'react-router-dom';
import { closeIndent } from './indentModalStore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import TatProgressPanel, { type TatProgress, type StageActionSpec } from './TatProgressPanel';
import IndentLifecycleControls from './IndentLifecycleControls';
import IndentEditPanel from './IndentEditPanel';
import { FulfilmentSummary, BatchList, useFulfilment } from './FulfilmentSummary';
import BatchEditModal from './BatchEditModal';
import { Fa } from './_icons';
import { api, fileUrl, openFileInNewTab } from '../../lib/api';
import { canAttachSerials } from './indentConstants';
import {
  RfqBroadcastModal,
  CompareAwardModal,
  Stage21Modal,
  LoiModal,
  LiveQuotesModal,
  SignedContractModal,
  GeneratePoModal,
  ProformaModal,
  PaymentRequestModal,
  StartTatModal,
  VesselAssignModal,
  DocVaultModal,
  FulfillmentDocsModal,
  ChaDispatchModal,
  OverrideStatusModal,
  NegotiationHistoryModal,
  BroadcastLogisticsRfqModal,
  AttachContainerModal,
  ExecuteLogisticsDispatchModal,
} from './IndentActions';
import { useAuth } from '../../lib/auth';
import { useAiEnabled, aiNegotiateDraft } from '../../lib/procurementAi';
import DataTable, { type Column } from '../ims/kit/DataTable';
import StatCards from '../ims/kit/StatCards';
import { Section, OpsPanel, Facts } from '../ims/kit/Chrome';

/**
 * Indent Detail — Control Tower.
 *
 * 1:1 port of the legacy Apps Script `renderIndentDetails(d)` modal:
 *   • Hero row — Indent ID + status badge + phase CTA + god-mode, with the
 *     green "Indent Total Value" on the right.
 *   • Live Agent Tracking — the 5-step timeline (On Water · Port Arrival ·
 *     In CFS · Customs · Final Depot) + bottleneck instruction badge.
 *   • Eight nav-pill tabs: 1. Blueprint · 2. Sourcing · 3. Ledger ·
 *     4. Tracker · 5. Vault · 6. Audit Log · 7. Inspection · 8. Gate-In.
 *
 * Used by both the standalone route (`/procurement/indents/:id`) and the
 * IndentDetailModal popup via `<IndentDetailContent inModal />`.
 */

type ModalKey =
  | 'rfq' | 'award' | 'loi' | 'contract' | 'stage21' | 'po' | 'pi'
  | 'payment' | 'tat' | 'vessel' | 'docvault' | 'cha' | 'fulfillment'
  | 'liveQuotes' | 'override' | 'negotiations' | 'negotiate'
  | 'logRfq' | 'attach' | 'dispatch' | null;

type TabKey = 'specs' | 'sourcing' | 'finance' | 'transit' | 'vault' | 'audit' | 'inspection' | 'gatein';

function fmtMoney(n: number, ccy: string) {
  if (!isFinite(n)) return `${ccy} 0`;
  return `${ccy} ${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

/** Dual-currency string — primary + " (≈ INR x)" when FX present. */
function dual(amount: number, ccy: string, exRate: number): string {
  const primary = fmtMoney(amount, ccy);
  if (ccy === 'INR' || !exRate || exRate <= 1) return primary;
  return `${primary} (≈ ${fmtMoney(amount * exRate, 'INR')})`;
}

function fmtDate(d: any) {
  if (!d || String(d).trim() === '') return 'N/A';
  try {
    const dt = new Date(d);
    if (isNaN(+dt)) return String(d);
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(d); }
}

/** N/A italic placeholder (source `safe`). */
function safeVal(v: any) {
  return (v !== null && v !== undefined && String(v).trim() !== '') ? String(v) : null;
}

export default function IndentDetail() {
  const { indentId = '' } = useParams();
  return <IndentDetailContent indentId={indentId} />;
}

export function IndentDetailContent({ indentId, inModal = false }: { indentId: string; inModal?: boolean }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [openModal, setOpenModal] = useState<ModalKey>(null);
  const [tab, setTab] = useState<TabKey>('specs');
  const [negotiateTarget, setNegotiateTarget] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery<{
    indent: any;
    quotations: any[];
    quotationItems: any[];
    payments: any[];
    docs: any[];
    containers: any[];
    negotiations: any[];
    acPipeline: Record<string, any>;
    /** Documents that exist only as links on the portal sheet (filed through
     *  the old Apps Script portal, so there is no in-app file to resolve). */
    sheetDocs?: { slots: Record<string, string>; additional: Array<{ label: string; url: string }> };
    /** Budget vs actual against the minimum-TAT schedule. Null plan = Import,
     *  which has no schedule yet. */
    tatProgress?: TatProgress;
    /** Stage 3.1 / 3.2 inspection sign-offs, read from the external survey
     *  workbook. These approvals never pass through this app's status chain,
     *  so they can only be read — see services/procurement/stage3-approvals.ts. */
    stage3Approvals?: Stage3Approvals;
  }>({
    queryKey: ['procurement', 'indent', indentId],
    queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}`),
    enabled: !!indentId,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <h5 className="mt-4 pc-text-muted font-bold">Summoning Control Tower…</h5>
        <p className="pc-text-secondary text-xs mt-1">Fetching global data for {indentId}</p>
      </div>
    );
  }
  if (error || !data?.indent) {
    return <div className="p-10 text-sm pc-text-danger">Failed to load {indentId} — {String((error as any)?.message || 'not found')}</div>;
  }

  const i = data.indent;
  const isImport = i.indentType === 'Import';
  const status: string = i.status || '';
  const qty = Number(i.quantity) || 0;
  const bids = data.quotationItems || [];

  // ── Dual-currency total derivation (mirrors legacy `displayTotalValue`).
  let grandTotal = 0;
  let totalAwardedQty = 0;
  let cur: string = i.awardedCurrency || i.expectedCurrency || 'INR';
  let exRate: number = Number(i.lockedExchangeRate) || Number(i.expectedExchangeRate) || 1;
  let lowestRate = Number.POSITIVE_INFINITY;
  let hasAwarded = false;
  bids.forEach((qi: any) => {
    const r = Number(qi.rate) || 0;
    if (r > 0 && r < lowestRate) lowestRate = r;
    if (qi.isSelected) {
      hasAwarded = true;
      const aq = Number(qi.awardedQty) || 0;
      grandTotal += r * aq;
      totalAwardedQty += aq;
      if (qi.currency) cur = qi.currency;
    }
  });

  let displayTotalValue: string;
  if (hasAwarded && totalAwardedQty > 0) {
    displayTotalValue = dual(grandTotal, cur, exRate);
  } else {
    const fallbackRate = Number(i.expectedRatePerContainer) || 0;
    const estCur = i.expectedCurrency || 'INR';
    const estEx = Number(i.expectedExchangeRate) || 1;
    displayTotalValue = (qty > 0 && fallbackRate > 0)
      ? dual(qty * fallbackRate, estCur, estEx) + '  (Est.)'
      : 'Pending PO Award';
  }

  // Status badge colour — bg-primary for shipment phases, else dark slate.
  const statusBg = status.includes('Shipment') ? '#052379' : '#010101';

  // ── 5-step Live Agent Tracking timeline (source getStageClass) ───────
  const stageClass = (stepNum: number): string => {
    let idx = -1;
    if (status === 'In Transit - On Water') idx = 0;
    else if (status === 'Arrived at Port') idx = 1;
    else if (status === 'In CFS') idx = 2;
    else if (status === 'Customs Cleared') idx = 3;
    else if (['Arrived at Depot', 'Awaiting Depot Verification', 'Depot Issue Reported'].includes(status)) idx = 4;
    else if (status === 'Pipeline Complete') idx = 5;
    if (idx === -1) return '';
    if (stepNum - 1 < idx) return 'completed';
    if (stepNum - 1 === idx) return 'active';
    return '';
  };

  // ── Bottleneck instruction (source switch) ───────────────────────────
  const BOTTLENECK: Record<string, string> = {
    'Indent Created': '<b>Action Required:</b> Broadcast RFQ to vendors to start collecting bids.',
    'RFQ in Progress': '<b>Action Required:</b> Wait for quotes, then Close RFQ Round to lock in bids.',
    'In Transit - On Water': '<b>Waiting on:</b> Agent to confirm Port Arrival.',
    'Arrived at Port': '<b>Waiting on:</b> Agent to confirm CFS movement.',
    'In CFS': '<b>Waiting on:</b> CHA to complete Customs Clearance.',
    'Customs Cleared': '<b>Action Required:</b> Send Verification Request to the Depot Manager.',
    'Arrived at Depot': '<b>Action Required:</b> Send Verification Request to the Depot Manager.',
    'Awaiting Depot Verification': '<b>Waiting on:</b> Depot Manager to confirm receipt and seal integrity via email.',
    'Depot Issue Reported': '<b>CRITICAL:</b> Depot Manager reported damage or broken seals.',
    'Pipeline Complete': '<b>Complete:</b> Container safely vaulted at depot. Pipeline closed.',
  };
  const bottleneckIcon: Record<string, string> = {
    'Indent Created': 'user-clock', 'RFQ in Progress': 'user-clock', 'Customs Cleared': 'user-clock',
    'Arrived at Depot': 'user-clock', 'Depot Issue Reported': 'exclamation-triangle',
    'Pipeline Complete': 'check-circle',
  };
  const bnText = BOTTLENECK[status];
  const bnIcon = bottleneckIcon[status] || 'hourglass-half';
  const bnIconColor =
    status === 'Depot Issue Reported' ? 'pc-text-danger'
      : status === 'Pipeline Complete' ? 'pc-text-success'
        : ['Indent Created', 'RFQ in Progress', 'Customs Cleared', 'Arrived at Depot'].includes(status) ? 'pc-text-primary'
          : 'pc-text-warning';

  // ── Phase-appropriate hero CTA (source ctaHtml switch) ───────────────
  const goCompare = () => { if (inModal) closeIndent(); navigate(`/procurement/indents/${encodeURIComponent(indentId)}/compare`); };
  const pingAgent = async () => {
    try { await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/ping-agent`, {}); toast.success('Agent pinged'); }
    catch (e: any) { toast.error(e?.message || 'Ping failed'); }
  };
  const requestVerify = async () => {
    try { await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/depot-dispatch`, {}); toast.success('Verification request sent'); }
    catch (e: any) { toast.error(e?.message || 'Dispatch failed'); }
  };

  let s = status;
  if (s === 'Awaiting Sales Contract' && !isImport) s = 'Awaiting 2.1 Submission';

  let cta: React.ReactNode = null;
  if (s === 'Indent Created') cta = <CtaBtn color="primary" icon="paper-plane" label="Broadcast RFQ" onClick={() => setOpenModal('rfq')} />;
  else if (s === 'RFQ in Progress') cta = <CtaBtn color="primary" icon="check-circle" label="Close RFQ" onClick={goCompare} />;
  else if (s === 'RFQ Completed' || s === 'Partially Awarded') cta = <CtaBtn color="warning" icon="scale-balanced" label="Compare & Award" onClick={goCompare} />;
  else if (s === 'Vendor Finalized') cta = <CtaBtn color="success" icon="file-signature" label="Issue LOI" onClick={() => setOpenModal('loi')} />;
  // Domestic 2.1 is the inspection email, not a contract upload — name the
  // work, so the button says what the operator is about to do.
  else if (s === 'Awaiting 2.1 Submission') cta = <CtaBtn color="dark" icon="envelope" label="Send container details for inspection" onClick={() => setOpenModal('stage21')} />;
  else if (s === 'Awaiting Sales Contract') cta = <CtaBtn color="dark" icon="file-upload" label="Upload Contract" onClick={() => setOpenModal('contract')} />;
  else if (s.includes('Under Inspection')) cta = <CtaBtn color="warning" icon="spinner" label="Awaiting Surveyors…" disabled />;
  else if (s === 'Inspection Complete - Request FMS') cta = <CtaBtn color="success" icon="paper-plane" label="Request FMS" onClick={() => setOpenModal('payment')} />;
  else if (s === 'Stage 2.1 Completed' || s === 'Contract Signed') cta = <CtaBtn color="warning" icon="file-invoice-dollar" label="Generate Draft PO" onClick={() => setOpenModal('po')} />;
  else if (s === 'PO Rejected') cta = <CtaBtn color="outline-danger" icon="redo" label="Revise & Resubmit" onClick={() => setOpenModal('po')} />;
  else if (s === 'PO Approved - Sent to Accounts' || s === 'Pending Advance Payment') cta = <CtaBtn color="primary" icon="paper-plane" label="Request FMS" onClick={() => setOpenModal('payment')} />;
  else if (s.includes('Ready for Shipment')) cta = <CtaBtn color="primary" icon="stopwatch" label="Set TAT Clock" onClick={() => setOpenModal('tat')} />;
  else if (s === 'Manufacturing in Progress') cta = <CtaBtn color="dark" icon="box-open" label="Upload Docs" onClick={() => setOpenModal('fulfillment')} />;
  else if (s === 'Manufacturing Complete') cta = (
    <div className="flex gap-2">
      <CtaBtn color="outline-primary" icon="broadcast-tower" label="Logistics RFQ" onClick={() => setOpenModal('logRfq')} />
      <CtaBtn color="dark" icon="ship" label="Final Dispatch" onClick={() => setOpenModal('dispatch')} />
    </div>
  );
  else if (s === 'Vessel Assigned - Awaiting Departure') cta = <CtaBtn color="primary" icon="file-invoice-dollar" label="Complete SIMS" onClick={() => setOpenModal('payment')} />;
  else if (s === 'SIMS Completed - Ready for Port') cta = (i.chaDispatched ? null : <CtaBtn color="dark" icon="file-export" label="Dispatch to CHA" onClick={() => setOpenModal('cha')} />);
  else if (s === 'In Transit - On Water') cta = <CtaBtn color="outline-primary" icon="bell" label="Ping Agent" onClick={pingAgent} />;
  else if (s === 'Customs Cleared' || s === 'Arrived at Depot') cta = <CtaBtn color="dark" icon="paper-plane" label="Request Verify" onClick={requestVerify} />;

  // Shared with the three list pages (see indentConstants) so the button can't
  // be offered here and missing there.
  const showAttachSerials = canAttachSerials({ status, lifecycleState: (i as any).lifecycleState });

  const canOverride = user?.role === 'admin';

  return (
    <div className={inModal ? 'space-y-5' : 'page'}>
      {/* ───────────────── Hero row ───────────────── */}
      <div className="page-head">
        <div style={{ flex: '1 1 420px', minWidth: 0 }}>
          <h1 className="page-title">{i.indentId}</h1>
          <p className="page-sub">Where has this order got to, and what does it need from us next?</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="pc-badge pc-shadow-sm" style={{ background: statusBg, color: '#fff', fontSize: '0.95rem', padding: '0.45rem 0.9rem' }}>{status || 'N/A'}</span>
            {cta}
            {/* Persistent "Attach Serials" — gated by STAGE, not by whether a
                vendor happens to be recorded, exactly as the reference does:
                shown for every status except the four pre-award sourcing ones.

                It used to require `awardedVendor`, which silently hid the button
                on any indent that had moved down the pipeline without a vendor
                written back (e.g. an import sitting at "Advance Requested" with
                a bid on file but no award recorded) — precisely the case where
                serials arrive and someone needs to key them in. Container
                numbers also turn up at different times, so the entry point has
                to stay available rather than track the award. */}
            {showAttachSerials && (
              <CtaBtn color="info" icon="barcode" label="Attach Serials" onClick={() => setOpenModal('attach')} />
            )}
            {/* Persistent "Generate PO" — reachable for the whole pipeline once a
                vendor is awarded, instead of only at the one status the primary
                CTA fires on. The screen itself refuses to cut a PO the flow is
                not ready for (server-side blockers), so this is discoverability,
                not a bypass. Hidden when the CTA above already offers it. */}
            {i.awardedVendor && s !== 'Stage 2.1 Completed' && s !== 'Contract Signed' && (
              <CtaBtn color="outline-primary" icon="file-invoice-dollar" label="Generate PO" onClick={() => setOpenModal('po')} />
            )}
            {canOverride && (
              <button
                type="button"
                title="Force Status Override"
                className="pc-btn pc-btn-sm pc-btn-light pc-shadow-sm press"
                onClick={() => setOpenModal('override')}
              >
                <Fa name="fast-forward" size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      <StatCards
        stats={[
          {
            key: 'value',
            label: 'Indent total value',
            value: <span className="text-xl">{displayTotalValue}</span>,
            sub: hasAwarded ? 'Awarded rate × awarded qty' : 'Estimate — nothing awarded yet',
            tone: hasAwarded ? 'good' : 'neutral',
          },
          {
            key: 'qty',
            label: 'Quantity',
            value: qty,
            sub: hasAwarded ? `${totalAwardedQty} unit(s) awarded so far` : 'Units requested',
          },
          {
            key: 'vendor',
            label: 'Awarded to',
            value: <span className="text-lg">{safeVal(i.awardedVendor) || 'Not awarded'}</span>,
            sub: bids.length ? `${bids.length} bid(s) on file` : 'No bids yet',
            tone: i.awardedVendor ? 'good' : 'warn',
          },
          {
            key: 'best',
            label: 'Best quoted rate',
            value: (
              <span className="text-lg">
                {Number.isFinite(lowestRate) ? fmtMoney(lowestRate, cur) : '—'}
              </span>
            ),
            sub: 'Lowest rate any vendor has quoted',
            tone: 'info',
          },
        ]}
      />

      {/* Park it, put it back, or stop it. Also the banner explaining a held
          or cancelled indent to everyone who opens it. */}
      <IndentLifecycleControls
        indentId={i.indentId}
        lifecycleState={(i as any).lifecycleState}
        lifecycle={(i as any).lifecycle}
      />

      {/* Correct the indent's own details, and walk back what was corrected. */}
      <IndentEditPanel indent={i} editLog={(i as any).editLog} />

      {/* How much of the TAT is spent, and which stage ate it. Hidden for
          Import until Operations hands over that schedule. */}
      {data.tatProgress?.plan && (
        <Section title="Is this order on time?" hint={`Minimum ${data.tatProgress.plan.minDays}-day TAT — budget vs actual, stage by stage`}>
          <TatProgressPanel
            p={data.tatProgress}
            stageActionsFor={(stage) => stageDeepLinks(stage, indentId, isImport, setOpenModal, setTab, navigate, { pingAgent, requestVerify })}
            signOffs={data.stage3Approvals?.stageMarks}
          />
        </Section>
      )}

      {/* ───────────────── Live Agent Tracking ─────────────────
          Import-only: the 5 legs (On Water → Port → CFS → Customs → Depot)
          are ocean-freight/customs-clearance stages that a Domestic purchase
          never passes through, so this whole section is hidden for them. */}
      {isImport && (
        <Section title="Where the shipment is" hint="Live agent tracking — the leg it has reached, and who we are waiting on">
          {/* 5-leg tracker — scrolls sideways inside the section on a phone
              instead of crushing each leg into 60px. */}
          <div className="overflow-x-auto">
            <div className="timeline-tracker" style={{ minWidth: 480 }}>
              <TimelineStep cls={stageClass(1)} icon="ship"         label="On Water" />
              <TimelineStep cls={stageClass(2)} icon="anchor"       label="Port Arrival" />
              <TimelineStep cls={stageClass(3)} icon="warehouse"    label="In CFS" />
              <TimelineStep cls={stageClass(4)} icon="stamp"        label="Customs" />
              <TimelineStep cls={stageClass(5)} icon="check-double" label="Final Depot" />
            </div>
          </div>
          {bnText && (
            <div className="text-center" style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #E4E7EE' }}>
              <span className="pc-badge pc-shadow-sm" style={{ background: '#F2F4F8', color: '#010101', border: '1px solid #E4E7EE', padding: '0.5rem 0.9rem', fontSize: '0.95rem' }}>
                <Fa name={bnIcon} size={12} className={bnIconColor} />
                <span style={{ marginLeft: 6 }} dangerouslySetInnerHTML={{ __html: bnText }} />
              </span>
            </div>
          )}
        </Section>
      )}

      {/* ───────────────── Nav pills ───────────────── */}
      <div id="tower-tabs" className="tower-pills" style={{ justifyContent: 'space-between' }}>
        <Pill active={tab === 'specs'}      onClick={() => setTab('specs')}      icon="box-open"            label="1. Blueprint" />
        <Pill active={tab === 'sourcing'}   onClick={() => setTab('sourcing')}   icon="comments-dollar"    label="2. Sourcing" />
        <Pill active={tab === 'finance'}    onClick={() => setTab('finance')}    icon="file-invoice-dollar" label="3. Ledger" />
        <Pill active={tab === 'transit'}    onClick={() => setTab('transit')}    icon="ship"               label="4. Tracker" />
        <Pill active={tab === 'vault'}      onClick={() => setTab('vault')}      icon="folder-open"        label="5. Vault" />
        <Pill active={tab === 'audit'}      onClick={() => setTab('audit')}      icon="history"            label="6. Audit Log" />
        <Pill active={tab === 'inspection'} onClick={() => setTab('inspection')} icon="clipboard-check"    label="7. Inspection" />
        <Pill active={tab === 'gatein'}     onClick={() => setTab('gatein')}     icon="warehouse"          label="8. Gate-In" />
      </div>

      {/* ───────────────── Tab content ───────────────── */}
      {tab === 'specs' && <BlueprintTab i={i} qty={qty} />}
      {tab === 'sourcing' && (
        <SourcingTab
          bids={bids}
          onNegotiate={(qi) => { setNegotiateTarget(qi); setOpenModal('negotiate'); }}
          onHistory={() => setOpenModal('negotiations')}
        />
      )}
      {tab === 'finance' && (
        <LedgerTab i={i} payments={data.payments || []} acPipeline={data.acPipeline || {}} onRequestFms={() => setOpenModal('payment')} />
      )}
      {tab === 'transit' && <TrackerTab i={i} containers={data.containers || []} indentId={indentId} />}
      {tab === 'vault' && <VaultTab i={i} indentId={indentId} docs={data.docs || []} sheetDocs={data.sheetDocs} />}
      {tab === 'audit' && <AuditTab i={i} />}
      {tab === 'inspection' && <InspectionTab indentId={indentId} approvals={data.stage3Approvals} />}
      {tab === 'gatein' && <GateInTab containers={data.containers || []} indentId={indentId} />}

      {/* ───────────────── Modals ───────────────── */}
      {openModal === 'rfq' && <RfqBroadcastModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'award' && <CompareAwardModal indentId={indentId} onClose={() => setOpenModal(null)} items={bids} indentQty={qty || 1} />}
      {openModal === 'loi' && <LoiModal indentId={indentId} onClose={() => setOpenModal(null)} awardedVendor={i.awardedVendor || ''} awardedVendorEmail={i.awardedVendorEmail || ''} indent={i} />}
      {openModal === 'liveQuotes' && <LiveQuotesModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'negotiations' && <NegotiationHistoryModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'contract' && <SignedContractModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'stage21' && <Stage21Modal indentId={indentId} indent={i} onClose={() => setOpenModal(null)} />}
      {openModal === 'po' && <GeneratePoModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'pi' && <ProformaModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'payment' && <PaymentRequestModal indentId={indentId} onClose={() => setOpenModal(null)} enquiryCaseNo={i.enquiryCaseNo} fmsBaseUrl={(window as any).__PROCUREMENT_FMS_URL__ || undefined} indentType={i.indentType || 'Import'} />}
      {openModal === 'tat' && <StartTatModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'vessel' && <VesselAssignModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'logRfq' && <BroadcastLogisticsRfqModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'attach' && <AttachContainerModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'dispatch' && <ExecuteLogisticsDispatchModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'docvault' && <DocVaultModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'fulfillment' && <FulfillmentDocsModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'cha' && <ChaDispatchModal indentId={indentId} onClose={() => setOpenModal(null)} />}
      {openModal === 'override' && <OverrideStatusModal indentId={indentId} onClose={() => setOpenModal(null)} currentStatus={status} isImport={isImport} />}
      {openModal === 'negotiate' && negotiateTarget && (
        <NegotiateModal
          indentId={indentId}
          item={negotiateTarget}
          onClose={() => { setOpenModal(null); setNegotiateTarget(null); }}
          onDone={() => qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] })}
        />
      )}
    </div>
  );
}

/* ════════════════════════ Tabs ════════════════════════ */

// 1. BLUEPRINT — Origin & Context + Asset Specifications + Technical Remarks
function BlueprintTab({ i, qty }: { i: any; qty: number }) {
  return (
    <div className="space-y-5">
      <Section title="Who asked for it" hint="Origin & context">
        <Facts
          items={[
            { label: 'Date created', value: <span className="text-teal-700">{fmtDate(i.createdAt)}</span> },
            { label: 'Category & type', value: <>{Badge(i.indentType, 'info')} {Badge(i.itemType, 'dark')}</> },
            { label: 'Triggering event', value: Safe(i.triggerType) },
            { label: 'Sales order ref', value: <span className="font-mono">{Safe(i.salesOrderNo)}</span> },
            { label: 'Purchaser / dept', value: <>{Safe(i.salesPersonName)} ({Safe(i.department)})</> },
            { label: 'Enquiry no.', value: Badge(i.enquiryCaseNo, 'secondary') },
            {
              label: 'Delivery location',
              value: <span className="pc-text-danger"><Fa name="map-marker-alt" size={11} /> {Safe(i.location || i.deliveryLocation)}</span>,
            },
          ]}
        />
      </Section>

      <Section title="What was asked for" hint="Asset specifications">
        <Facts
          items={[
            { label: 'Item / code', value: <>{Badge(i.itemType, 'dark')} {Badge(i.productCode, 'secondary')}</> },
            { label: 'Quantity required', value: <span className="text-teal-700">{qty} Units</span> },
            { label: 'Size', value: Safe(i.sizeAllDetails) },
            { label: 'Condition / grade', value: <>{Safe(i.assetCondition)} | {Safe(i.grade)}</> },
            { label: 'Industry / purpose', value: <>{Safe(i.industry)} | {Safe(i.purposeOfBuying)}</> },
          ]}
        />
      </Section>

      <Section title="Technical requirements & remarks" hint="Everything the buyer wrote in free text">
        <p className="pc-text-muted" style={{ fontWeight: 700, fontSize: '0.8rem', margin: '0 0 4px' }}>Technical Specifications:</p>
        <div style={{ padding: 8, background: '#F2F4F8', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem' }} className="font-mono pc-text-dark">{Safe(i.specifications)}</div>
        <p className="pc-text-muted" style={{ fontWeight: 700, fontSize: '0.8rem', margin: '0 0 4px' }}>Accessories Required:</p>
        <div style={{ padding: 8, background: '#F2F4F8', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem' }} className="pc-text-dark">{Safe(Array.isArray(i.accessories) ? i.accessories.join(', ') : i.accessories)}</div>
        <p className="pc-text-muted" style={{ fontWeight: 700, fontSize: '0.8rem', margin: '0 0 4px' }}>Sales Remarks & Container Pref:</p>
        <div style={{ padding: 8, background: '#F2F4F8', borderRadius: 6, fontSize: '0.85rem' }} className="pc-text-dark">{Safe(i.salesPersonRemarks)} | {Safe(i.containerPreference)}</div>
      </Section>
    </div>
  );
}

// 2. SOURCING — Quotation & Vendor Bids table
function SourcingTab({ bids, onNegotiate, onHistory }: { bids: any[]; onNegotiate: (qi: any) => void; onHistory: () => void }) {
  const columns: Array<Column<any>> = [
    {
      key: 'vendor',
      header: 'Vendor / supplier',
      grow: true,
      value: (bid) => bid.vendorName || '',
      render: (bid) => <span className="font-semibold text-slate-800">{Safe(bid.vendorName)}</span>,
      // The awarded bid keeps its green wash, as in the legacy table.
      cellClass: (bid) => (bid.isSelected ? 'bg-emerald-50' : ''),
    },
    {
      key: 'tier',
      header: 'Tier (qty & YOM)',
      align: 'center',
      value: (bid) => Number(bid.quotedQty) || 0,
      render: (bid) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <span className="pc-badge pc-bg-info">{bid.quotedQty || 'All'} Units</span>
          <span className="pc-badge" style={{ border: '1px solid #6B7280', color: '#010101' }}>YOM: {bid.yom || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'rate',
      header: 'Quoted rate & actions',
      value: (bid) => Number(bid.rate) || 0,
      render: (bid) => (
        <div>
          <span style={{ fontWeight: 700, fontSize: '1rem' }} className="pc-text-dark">
            {dual(Number(bid.rate) || 0, bid.currency || 'INR', Number(bid.exchangeRate) || 1)}
          </span>
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="pc-btn pc-btn-sm pc-btn-outline-warning press" style={{ padding: '0 8px', fontSize: '0.75rem' }} title="Negotiate" onClick={() => onNegotiate(bid)}>
              <Fa name="gavel" size={10} /> Negotiate
            </button>
            <button className="pc-btn pc-btn-sm pc-btn-outline-secondary press" style={{ padding: '0 8px', border: 'none' }} title="View History" onClick={onHistory}>
              <Fa name="history" size={12} />
            </button>
          </div>
        </div>
      ),
    },
    { key: 'delivery', header: 'Delivery time', value: (bid) => bid.deliveryTime || '', render: (bid) => Safe(bid.deliveryTime) },
    {
      key: 'payment',
      header: 'Payment terms',
      grow: true,
      value: (bid) => bid.paymentTerms || '',
      render: (bid) => <span className="pc-text-muted">{Safe(bid.paymentTerms)}</span>,
    },
    {
      key: 'doc',
      header: 'Doc',
      align: 'center',
      noExport: true,
      render: (bid) =>
        bid.fileId ? (
          <a
            href={fileUrl(bid.fileId)}
            onClick={(e) => { e.preventDefault(); openFileInNewTab(String(bid.fileId)).catch(() => toast.error('Could not open quotation')); }}
            target="_blank"
            rel="noreferrer"
            className="pc-btn pc-btn-sm pc-btn-outline-info press"
          >
            <Fa name="file-pdf" size={11} />
          </a>
        ) : (
          '-'
        ),
    },
    {
      key: 'award',
      header: 'Award status',
      value: (bid) => (bid.isSelected ? 'Awarded' : bid.status === 'pending' ? 'Pending' : 'Open'),
      render: (bid) =>
        bid.isSelected ? (
          <span className="pc-badge pc-bg-success"><Fa name="trophy" size={10} /> Awarded ({bid.awardedQty || 'All'} Units)</span>
        ) : bid.status === 'pending' ? (
          <span className="pc-badge pc-bg-warning"><Fa name="hourglass-half" size={10} /> Pending</span>
        ) : (
          <span className="pc-badge pc-bg-secondary">Open</span>
        ),
    },
  ];

  return (
    <Section title="Who bid, and for how much" hint="Quotation & vendor bids" flush>
      {bids.length === 0 ? (
        <div className="text-center pc-text-muted" style={{ padding: '3rem 1rem' }}>
          <Fa name="search-dollar" size={36} className="pc-text-muted" />
          <h5 style={{ fontWeight: 700, marginTop: 12 }}>No Quotations Found</h5>
          <p style={{ fontSize: '0.85rem' }}>Sourcing data has not been logged for this indent yet.</p>
        </div>
      ) : (
        <DataTable<any>
          rows={bids}
          columns={columns}
          rowKey={(bid) => bid.itemId}
          exportName="vendor-bids"
          searchPlaceholder="Search vendor, terms…"
          maxHeight="400px"
          emptyText="No Quotations Found"
        />
      )}
    </Section>
  );
}

// 3. LEDGER — Financial Ledger & Landed Cost Tracker
const LEDGER_ROWS: Array<{ key: string; label: string }> = [
  { key: 'Advance_30', label: '30% Container Advance' }, { key: 'Balance_70', label: '70% Container Balance' }, { key: 'Full_Payment', label: '100% Full Payment' },
  { key: 'Insurance', label: 'Insurance Payment' }, { key: 'Freight', label: 'Freight Payment' },
  { key: 'Local_Agent', label: 'Local Agent Charges' }, { key: 'SIMS', label: 'SIMS Payment' },
  { key: 'Customs_Duty', label: 'Custom Duty Payment' }, { key: 'Stamp_Duty', label: 'Stamp Duty Payment' },
  { key: 'CFS', label: 'CFS Charges' }, { key: 'CHA', label: 'CHA Charges' },
];

// Domestic indents never touch ocean freight / customs clearance / SIMS /
// stamp duty etc. — those categories are import-only. Show just the two
// payment types that actually apply to a domestic purchase.
const DOMESTIC_LEDGER_KEYS = new Set(['Full_Payment', 'Customs_Duty']);

// Real-world Accounts pipeline status for one ledger row (bridged via
// PurchaseToFinance → PURCHASEnew → NewACCOUNTSFMS — see ac-status-pipeline.ts
// on the backend). Distinct from "FMS status" (our own app's Requested/Paid
// tracking) — this is what Accounts itself says is happening with the money.
function AcStatusCell({ ac }: { ac: any }) {
  if (!ac) return <span className="pc-text-muted" style={{ fontSize: '0.75rem' }}>—</span>;
  const label = ac.acStatus || ac.overallStatus || ac.ptfStatus || (ac.vrNo ? 'VR Generated' : 'Submitted');
  const tone = /paid|approved|clear/i.test(label) ? 'pc-bg-success' : /reject/i.test(label) ? 'pc-bg-danger' : 'pc-bg-warning';
  return (
    <div style={{ fontSize: '0.75rem' }} title={ac.remark || ac.accountsRemarks || ac.ceoRemarks || ''}>
      <span className={`pc-badge ${tone}`}>{label}</span>
      {(ac.vrNo || ac.utr) && (
        <div className="pc-text-muted" style={{ marginTop: 2 }}>
          {ac.vrNo ? `VR ${ac.vrNo}` : ''}{ac.vrNo && ac.utr ? ' · ' : ''}{ac.utr ? `UTR ${ac.utr}` : ''}
        </div>
      )}
    </div>
  );
}

function LedgerTab({ i, payments, acPipeline, onRequestFms }: { i: any; payments: any[]; acPipeline: Record<string, any>; onRequestFms: () => void }) {
  const ledgerCur = i.awardedCurrency || i.expectedCurrency || 'INR';
  const ledgerEx = Number(i.lockedExchangeRate) || Number(i.expectedExchangeRate) || 1;
  const isDomestic = i.indentType === 'Domestic';
  const ledgerRows = isDomestic ? LEDGER_ROWS.filter((r) => DOMESTIC_LEDGER_KEYS.has(r.key)) : LEDGER_ROWS;
  // Index payments by type → latest row.
  // Index by type, preferring the row with the most signal (max of requested /
  // paid) so a later batch request with amount 0 doesn't mask a real amount.
  const byType = new Map<string, any>();
  const sig = (x: any) => Math.max(Number(x?.requestedAmount) || 0, Number(x?.paidAmount) || 0);
  payments.forEach((p) => { const ex = byType.get(p.paymentType); if (!ex || sig(p) > sig(ex)) byType.set(p.paymentType, p); });

  let totalReq = 0, totalPaid = 0, totalBal = 0;
  const rows = ledgerRows.map((pt) => {
    const p = byType.get(pt.key);
    const statusRaw = p?.status || '';
    const reqAmt = Number(p?.requestedAmount) || 0;
    const paidAmt = Number(p?.paidAmount) || 0;
    const balance = reqAmt - paidAmt;
    const counted = ['requested', 'awaiting_payment', 'paid', 'partially_paid'].includes(statusRaw) || statusRaw.toLowerCase().includes('paid');
    if (counted) { totalReq += reqAmt; totalPaid += paidAmt; totalBal += balance; }

    let badge: React.ReactNode = <span className="pc-badge" style={{ background: '#F2F4F8', color: '#6B7280', border: '1px solid #E4E7EE' }}>Not Requested</span>;
    if (statusRaw === 'requested') badge = <span className="pc-badge pc-bg-warning"><Fa name="hourglass-half" size={10} /> Requested</span>;
    else if (statusRaw === 'awaiting_payment') badge = <span className="pc-badge pc-bg-primary"><Fa name="clock" size={10} /> Awaiting Payment</span>;
    else if (statusRaw === 'paid' || statusRaw === 'partially_paid' || statusRaw.toLowerCase().includes('paid')) badge = <span className="pc-badge pc-bg-success"><Fa name="check-circle" size={10} /> Paid</span>;

    const balColor = balance === 0 ? 'pc-text-muted' : (balance > 0 ? 'pc-text-warning' : 'pc-text-danger');
    const ac = acPipeline?.[pt.key];
    return (
      <tr key={pt.key} style={{ borderBottom: '1px solid #F2F4F8' }}>
        <td style={{ fontWeight: 700, paddingLeft: '1.25rem' }} className="pc-text-dark">{pt.label}</td>
        <td style={{ textAlign: 'center' }}>{badge}</td>
        <td style={{ textAlign: 'center' }}><AcStatusCell ac={ac} /></td>
        <td style={{ textAlign: 'right', fontWeight: 700 }} className="pc-text-secondary">{reqAmt > 0 ? dual(reqAmt, ledgerCur, ledgerEx) : '-'}</td>
        <td style={{ textAlign: 'right', fontWeight: 700 }} className="pc-text-success">{paidAmt > 0 ? dual(paidAmt, ledgerCur, ledgerEx) : '-'}</td>
        <td style={{ textAlign: 'right', fontWeight: 700, paddingRight: '1.25rem' }} className={balColor}>{(reqAmt > 0 || paidAmt > 0) ? dual(balance, ledgerCur, ledgerEx) : '-'}</td>
      </tr>
    );
  });

  // All FMS requests — EVERY attempt (all rows, not just latest-per-type) with
  // its Accounts status + reason. Mirrors the reference "All requests — N tries".
  const labelOf = (key: string) => LEDGER_ROWS.find((r) => r.key === key)?.label || String(key || '').replace(/_/g, ' ');
  const sorted = [...payments].sort((a, b) => new Date(a.createdAt || a.requestedAt || 0).getTime() - new Date(b.createdAt || b.requestedAt || 0).getTime());
  const tryCount = new Map<string, number>();
  const attemptRows = sorted.map((p) => { const n = (tryCount.get(p.paymentType) || 0) + 1; tryCount.set(p.paymentType, n); return { p, tryNo: n }; });
  const attemptBadge = (s: string) => {
    const v = String(s || '').toLowerCase();
    if (v === 'rejected') return <span className="pc-badge pc-bg-danger"><Fa name="times-circle" size={10} /> Rejected</span>;
    if (v === 'paid' || v === 'partially_paid' || v.includes('paid')) return <span className="pc-badge pc-bg-success"><Fa name="check-circle" size={10} /> Paid</span>;
    return <span className="pc-badge pc-bg-warning"><Fa name="hourglass-half" size={10} /> Pending</span>;
  };

  return (
    <div className="space-y-5">
      <Section
        title="What this order has cost us"
        hint="Financial ledger & landed-cost tracker"
        right={
          <button className="btn press inline-flex bg-teal-600 px-3 py-1.5 text-xs text-white hover:bg-teal-700" onClick={onRequestFms}>
            <Fa name="paper-plane" size={11} /> Request FMS Payment
          </button>
        }
        flush
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="grid-table">
            <thead>
              <tr>
                <th>Payment category</th>
                <th style={{ textAlign: 'center' }}>FMS status</th>
                <th style={{ textAlign: 'center' }}>Real A/C Status</th>
                <th className="num">Requested</th>
                <th className="num">Actual paid</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
            <tfoot style={{ background: '#F2F4F8' }}>
              <tr>
                <td colSpan={3} style={{ paddingLeft: '1.25rem', fontWeight: 700, textTransform: 'uppercase' }} className="pc-text-dark">Total Indent Spend</td>
                <td style={{ textAlign: 'right', fontWeight: 700 }} className="pc-text-dark">{dual(totalReq, ledgerCur, ledgerEx)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, fontSize: '1.05rem' }} className="pc-text-success">{dual(totalPaid, ledgerCur, ledgerEx)}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, paddingRight: '1.25rem' }} className={totalBal === 0 ? 'pc-text-muted' : 'pc-text-danger'}>{dual(totalBal, ledgerCur, ledgerEx)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>

      {attemptRows.length > 0 && (
        <Section
          title={`Every FMS request — ${attemptRows.length} ${attemptRows.length === 1 ? 'try' : 'tries'}`}
          hint="Each attempt, what Accounts said, and why"
          flush
        >
          <div>
            {attemptRows.map(({ p, tryNo }, idx) => (
              <div key={p._id || idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '0.75rem 1.25rem', borderBottom: '1px solid #F2F4F8' }}>
                <div style={{ minWidth: 150, fontWeight: 700 }} className="pc-text-dark">
                  {labelOf(p.paymentType)} <span className="pc-text-muted" style={{ fontWeight: 500 }}>· Try {tryNo}</span>
                </div>
                <div style={{ minWidth: 110 }}>{attemptBadge(p.status)}</div>
                <div style={{ flex: 1, fontSize: '0.85rem' }} className="pc-text-secondary">
                  {p.remarks
                    ? <span style={{ fontStyle: 'italic' }}>“{p.remarks}”</span>
                    : <span className="pc-text-muted">— no remark yet —</span>}
                  {(Number(p.requestedAmount) > 0 || Number(p.paidAmount) > 0) && (
                    <span className="pc-text-muted" style={{ marginLeft: 8, fontSize: '0.78rem' }}>
                      (req {Number(p.requestedAmount || 0).toLocaleString('en-IN')}{Number(p.paidAmount) > 0 ? ` · paid ${Number(p.paidAmount).toLocaleString('en-IN')}` : ''})
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <OpsPanel title="Ops details" hint="Ledger currency, locked FX and the raw payment-type keys">
        <Facts
          items={[
            { label: 'Ledger currency', value: <span className="font-mono">{ledgerCur}</span> },
            { label: 'FX used (→ INR)', value: <span className="font-mono">{ledgerEx}</span> },
            { label: 'Payment rows on file', value: payments.length },
            { label: 'Categories tracked', value: ledgerRows.length },
          ]}
        />
        <div className="flex flex-wrap gap-1">
          {ledgerRows.map((r) => (
            <span key={r.key} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 font-mono text-[10.5px] text-slate-600">
              {r.key}
            </span>
          ))}
        </div>
      </OpsPanel>
    </div>
  );
}

// 4. TRACKER — Master tracking banner + Logistics Partners + Vessel Matrix
function TrackerTab({ i, containers, indentId }: { i: any; containers: any[]; indentId: string }) {
  const containerNos = (containers.map((c) => c.containerNumber || c.serialNumber).filter(Boolean).join(', ')) || i.containerNumbers;
  const { data: fulfilment } = useFulfilment(indentId);
  const [editing, setEditing] = useState<string | null>(null);
  return (
    <div className="space-y-5">
      <Section title="How much of this order has moved" hint="Ordered vs shipped vs received">
        <FulfilmentSummary f={fulfilment} stage="all" />
      </Section>

      {!!fulfilment?.batches.length && (
        <Section
          title="Sailings"
          hint={
            fulfilment.batches.length === 1
              ? 'One shipment'
              : `${fulfilment.batches.length} shipments — each with its own vessel, voyage and ETA`
          }
        >
          <BatchList f={fulfilment} onEdit={(id) => setEditing(id)} />
          {fulfilment.pendingShipment > 0 && (
            <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-900">
              {fulfilment.pendingShipment} container{fulfilment.pendingShipment === 1 ? '' : 's'} still to book onto a vessel.
            </div>
          )}
        </Section>
      )}
      {editing && (
        <BatchEditModal
          batch={fulfilment!.batches.find((b) => b.id === editing)!}
          indentId={indentId}
          onClose={() => setEditing(null)}
        />
      )}
      <Section title="What is being shipped" hint="Master tracking / container numbers">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p className="pc-text-info" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: '0 0 4px' }}>Master Tracking / Container Numbers</p>
            <h5 className="font-mono pc-text-dark" style={{ fontWeight: 700, margin: 0 }}>{Safe(containerNos)}</h5>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p className="pc-text-info" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: '0 0 4px' }}>Origin Pickup Location</p>
            <h6 className="pc-text-dark" style={{ fontWeight: 700, margin: 0 }}><Fa name="map-marker-alt" size={12} className="pc-text-danger" /> {Safe(i.pickupLocation)}</h6>
          </div>
        </div>
      </Section>

      <Section title="Who is moving it" hint="Logistics partners">
        <Facts
          items={[
            {
              label: 'Freight forwarder',
              value: (
                <span>
                  <Fa name="ship" size={12} className="pc-text-primary" /> {Safe(i.freightName || i.freightForwarderEmail)}
                  {i.freightName ? <span className="pc-text-secondary"> · {Safe(i.freightForwarderEmail)}</span> : null}
                </span>
              ),
            },
            {
              label: 'Insurance provider',
              value: (
                <span>
                  <Fa name="shield-alt" size={12} className="pc-text-success" /> {Safe(i.insuranceName || i.insuranceEmail)}
                  {i.insuranceName ? <span className="pc-text-secondary"> · {Safe(i.insuranceEmail)}</span> : null}
                </span>
              ),
            },
            {
              label: 'Local agent',
              value: <span><Fa name="user" size={12} className="pc-text-info" /> {Safe(i.assignedLocalAgent)}</span>,
            },
          ]}
        />
      </Section>

      {/* The indent header mirrors the LATEST sailing. With more than one it is
          only part of the story, so say so and point at the table above rather
          than letting someone read batch 2's ETA as the whole order's. */}
      <Section
        title={fulfilment && fulfilment.batches.length > 1 ? 'Latest sailing' : 'The vessel it is on'}
        hint={fulfilment && fulfilment.batches.length > 1
          ? `Most recent of ${fulfilment.batches.length} — see Sailings above for the rest`
          : 'Vessel matrix'}
      >
        <Facts
          items={[
            { label: 'Shipping line', value: Safe(i.shippingLine) },
            { label: 'Vessel name', value: Safe(i.vesselName) },
            { label: 'BL no.', value: <span className="font-mono text-teal-700">{Safe(i.blNumber)}</span> },
            { label: 'Left port (ETD)', value: fmtDate(i.etdDeparture) },
            { label: 'Due to arrive (ETA)', value: fmtDate(i.etaArrival) },
          ]}
        />
      </Section>
    </div>
  );
}

// 5. VAULT — Master Document Vault grouped doc buttons.
// Each fixed slot uploads DIRECTLY when pending (reference uploadVaultDocumentOmni,
// standard branch); "+ Add Additional Document" adds a custom-labelled doc
// directly (isCustom branch). No HOD approval gate — a plain vault upload.
function VaultTab({ i, indentId, docs, sheetDocs }: { i: any; indentId: string; docs: any[]; sheetDocs?: { slots: Record<string, string>; additional: Array<{ label: string; url: string }> } }) {
  // Documents filed on the portal sheet but never uploaded through the app.
  const sd = sheetDocs?.slots || {};
  // Merge direct custom docs (indent.additionalDocs) with the HOD approval-vault
  // entries (data.docs). Once an approval doc is APPROVED, the vault shows the
  // e-signed / certified copy (mergedFileId) in place of the original upload —
  // the approved doc replaces the unapproved one here.
  const extra = [
    ...((i.additionalDocs || []).map((d: any) => ({ key: d._id || d.fileId, label: d.label, fileId: d.fileId, status: '' as string, pendingWith: [] as { name: string; email: string }[] }))),
    ...docs.map((d: any) => ({
      key: d.docId || d._id,
      label: d.customLabel || d.docType,
      fileId: d.status === 'approved' ? (d.mergedFileId || d.fileId) : d.fileId,
      status: String(d.status || ''),                 // pending_hod | approved | rejected
      pendingWith: (d.pendingWith || []) as { name: string; email: string }[],
    })),
  ].filter((d) => d.fileId);

  // Extra documents that exist only as links on the sheet. Deduped against
  // what we already show by label, so an app upload never appears twice.
  const seenLabels = new Set(extra.map((d) => String(d.label || '').trim().toLowerCase()));
  const sheetExtra = (sheetDocs?.additional || []).filter(
    (d) => !seenLabels.has(String(d.label || '').trim().toLowerCase()),
  );
  return (
    <Section title="Every document we hold" hint="Master document vault — red means we are still missing it">
      <div>
        <VaultGroup title="Purchase Orders">
          <VaultBtn url={i.poFileId} label="Purchase Order" icon="file-contract" slot="purchaseOrder" indentId={indentId} href={sd.poFileId} />
        </VaultGroup>
        <VaultGroup title="Commercial & Finance">
          <VaultBtn url={i.ourContractFileId} label="Our Signed Contract" icon="file-signature" slot="ourContract" indentId={indentId} href={sd.ourContractFileId} />
          <VaultBtn url={i.vendorContractFileId} label="Vendor Signed Contract" icon="handshake" slot="vendorContract" indentId={indentId} href={sd.vendorContractFileId} />
          <VaultBtn url={i.proformaInvoiceFileId} label="Proforma Invoice" icon="file-invoice" slot="proformaInvoice" indentId={indentId} href={sd.proformaInvoiceFileId} />
          <VaultBtn url={i.finalInvoiceFileId} label="Tax Invoice" icon="file-invoice-dollar" slot="commercialInvoice" indentId={indentId} href={sd.finalInvoiceFileId} />
          <VaultBtn url={i.simsCertFileId} label="SIMS Certificate" icon="file-contract" slot="simsCert" indentId={indentId} href={sd.simsCertFileId} />
          <VaultBtn url={i.dutyReceiptFileId} label="Customs Duty Receipt" icon="receipt" slot="dutyReceipt" indentId={indentId} href={sd.dutyReceiptFileId} />
          <VaultBtn url={i.paymentReceiptFileId} label="Payment Receipt" icon="receipt" slot="paymentReceipt" indentId={indentId} href={sd.paymentReceiptFileId} />
          <VaultBtn url={i.containerCostingFileId} label="Container Costing" icon="file-invoice-dollar" slot="containerCosting" indentId={indentId} href={sd.containerCostingFileId} />
        </VaultGroup>
        <VaultGroup title="Logistics & Customs Clearance">
          <VaultBtn url={i.billOfLadingFileId} label="Bill of Lading (BL)" icon="file-pdf" slot="billOfLading" indentId={indentId} href={sd.billOfLadingFileId} />
          <VaultBtn url={i.billOfEntryFileId} label="Bill of Entry" icon="file-import" slot="billOfEntry" indentId={indentId} href={sd.billOfEntryFileId} />
          <VaultBtn url={i.packingListFileId} label="Packing List" icon="boxes" slot="packingList" indentId={indentId} href={sd.packingListFileId} />
          <VaultBtn url={i.insuranceDocFileId} label="Insurance Policy" icon="shield-alt" slot="insurance" indentId={indentId} href={sd.insuranceDocFileId} />
          <VaultBtn url={i.cooDocFileId} label="Certificate of Origin (COO)" icon="globe" slot="coo" indentId={indentId} href={sd.cooDocFileId} />
        </VaultGroup>
        <VaultGroup title="Technical & Manufacturing">
          <VaultBtn url={i.cscDocFileId} label="CSC Certificate" icon="certificate" slot="cscCert" indentId={indentId} href={sd.cscDocFileId} />
          <VaultBtn url={i.photosZipFileId} label="Container Photos (.zip)" icon="file-archive" slot="photosZip" indentId={indentId} href={sd.photosZipFileId} />
          <VaultBtn url={i.commDocFileId} label="Commissionary Certificate" icon="file-signature" slot="commissionary" indentId={indentId} href={sd.commDocFileId} />
          <VaultBtn url={i.testCertFileId} label="Test Certificate" icon="vial" slot="testCert" indentId={indentId} href={sd.testCertFileId} />
        </VaultGroup>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
          <p className="pc-text-muted" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: 0 }}>Additional Documents</p>
          <AddCustomDoc indentId={indentId} />
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', background: '#F2F4F8', padding: 12, borderRadius: 8, border: '1px solid #E4E7EE' }}>
          {extra.length === 0 ? (
            <span className="pc-text-muted" style={{ fontSize: '0.85rem', fontStyle: 'italic' }}>No additional documents uploaded yet. Click the button above to add one.</span>
          ) : (
            extra.map((d) => {
              const approved = d.status === 'approved';
              const pending = d.status === 'pending_hod';
              const rejected = d.status === 'rejected';
              return (
                <a
                  key={d.key}
                  href={fileUrl(d.fileId)}
                  onClick={(e) => { e.preventDefault(); openFileInNewTab(String(d.fileId)).catch(() => toast.error('Could not open document')); }}
                  target="_blank"
                  rel="noreferrer"
                  className={`pc-btn pc-btn-sm pc-shadow-sm ${approved ? 'pc-btn-outline-success' : rejected ? 'pc-btn-outline-danger' : 'pc-btn-outline-primary'}`}
                  style={{ marginBottom: 8 }}
                  title={
                    approved ? 'HOD-approved — e-signed copy'
                    : pending ? `Awaiting approval from ${d.pendingWith.length ? d.pendingWith.map((a: any) => a.name).join(', ') : 'Purchase HOD'}`
                    : rejected ? 'Rejected by Purchase HOD' : undefined
                  }
                >
                  <Fa name={approved ? 'stamp' : 'file-alt'} size={11} /> {d.label}
                  {approved && <span className="pc-badge pc-bg-success" style={{ marginLeft: 6, fontSize: 9 }}>✓ e-signed</span>}
                  {pending && (
                    <span className="pc-badge pc-bg-warning" style={{ marginLeft: 6, fontSize: 9 }}>
                      Pending HOD{d.pendingWith.length ? ` · ${d.pendingWith[0].name}` : ''}
                    </span>
                  )}
                  {rejected && <span className="pc-badge pc-bg-danger" style={{ marginLeft: 6, fontSize: 9 }}>Rejected</span>}
                </a>
              );
            })
          )}
          {sheetExtra.map((d) => (
            <a
              key={`sheet-${d.label}-${d.url}`}
              href={d.url}
              target="_blank"
              rel="noreferrer"
              className="pc-btn pc-btn-sm pc-shadow-sm pc-btn-outline-success"
              style={{ marginBottom: 8 }}
              title={`${d.label} — filed on the portal sheet`}
            >
              <Fa name="file-alt" size={11} /> {d.label}
              <span className="pc-badge pc-bg-success" style={{ marginLeft: 6, fontSize: 9 }}>sheet</span>
            </a>
          ))}
        </div>
      </div>
    </Section>
  );
}

// Direct custom-document add (reference isCustom branch) — inline label + file,
// no HOD approval, appends to indent.additionalDocs.
function AddCustomDoc({ indentId }: { indentId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async (f: File) => {
    if (!label.trim()) { toast.error('Enter a document name first'); return; }
    setBusy(true);
    try {
      const up = await api.upload<{ id: string }>('/api/files/upload', f);
      await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/vault-doc`, { custom: true, label: label.trim(), fileId: up.id });
      toast.success('Document added to vault');
      setLabel(''); setOpen(false);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
    } catch (e: any) { toast.error(e?.message || 'Upload failed'); } finally { setBusy(false); }
  };
  if (!open) return <button className="pc-btn pc-btn-sm pc-btn-outline-primary" onClick={() => setOpen(true)}><Fa name="plus" size={11} /> Add Additional Document</button>;
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <input className="pc-input w-full sm:w-[200px]" placeholder="Document name (e.g. CFS Invoice)" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
      <label className="pc-btn pc-btn-sm pc-btn-primary" style={{ cursor: busy ? 'wait' : 'pointer', opacity: busy || !label.trim() ? 0.6 : 1 }}>
        <Fa name={busy ? 'spinner' : 'upload'} size={11} /> {busy ? 'Uploading…' : 'Choose file & add'}
        <input type="file" hidden disabled={busy || !label.trim()} onChange={(e) => { const f = e.target.files?.[0]; if (f) void add(f); e.currentTarget.value = ''; }} />
      </label>
      <button className="pc-btn pc-btn-sm pc-btn-outline-secondary" onClick={() => { setOpen(false); setLabel(''); }}>Cancel</button>
    </div>
  );
}

// 6. AUDIT LOG — TAT clock + Assigned Actors + Dispatch Log
function AuditTab({ i }: { i: any }) {
  return (
    <div className="space-y-5">
      <Section title="Is the vendor on time?" hint="Manufacturing TAT clock">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div style={{ textAlign: 'center', paddingRight: 16 }} className="sm:border-r sm:border-slate-200">
            <p className="pc-text-muted" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: '0 0 4px' }}>Start Date</p>
            <h5 style={{ fontWeight: 700, margin: 0 }}>{fmtDate(i.tatStartDate)}</h5>
          </div>
          <div style={{ textAlign: 'center', padding: '0 16px', flexGrow: 1 }}>
            <p className="pc-text-muted" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: '0 0 4px' }}>Total Promised Days</p>
            <h2 className="pc-text-primary" style={{ fontWeight: 700, margin: 0 }}>{Safe(i.tatDays) || '—'}</h2>
          </div>
          <div style={{ textAlign: 'center', paddingLeft: 16 }} className="sm:border-l sm:border-slate-200">
            <p className="pc-text-muted" style={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.8rem', margin: '0 0 4px' }}>Deadline Date</p>
            <h5 className="pc-text-danger" style={{ fontWeight: 700, margin: 0 }}>{fmtDate(i.tatEndDate)}</h5>
          </div>
        </div>
      </Section>

      <Section title="Who owns this order" hint="Assigned system actors">
        <Facts
          items={[
            { label: 'Internal controller email', value: Safe(i.assignedEmail) },
            { label: 'External vendor email', value: Safe(i.awardedVendorEmail || i.vendorEmail) },
          ]}
        />
      </Section>

      <Section title="What the system has chased" hint="Automated dispatch log — reminders fired at each TAT milestone" flush>
        <div className="overflow-x-auto">
        <table className="grid-table">
          <thead>
            <tr><th>Milestone</th><th>Notification type</th><th className="num">Timestamp fired</th></tr>
          </thead>
          <tbody>
            <tr><td style={{ fontWeight: 700 }}>50% Timeline</td><td><span className="pc-badge pc-bg-info">Warning Alert</span></td><td className="num pc-text-muted font-mono">{Safe(i.reminder50Sent) || 'N/A'}</td></tr>
            <tr><td style={{ fontWeight: 700 }}>75% Timeline</td><td><span className="pc-badge pc-bg-warning">Urgent Alert</span></td><td className="num pc-text-muted font-mono">{Safe(i.reminder75Sent) || 'N/A'}</td></tr>
            <tr><td style={{ fontWeight: 700 }} className="pc-text-danger">100% Deadline</td><td><span className="pc-badge pc-bg-danger">Critical Alert</span></td><td className="num pc-text-muted font-mono">{Safe(i.reminder100Sent) || 'N/A'}</td></tr>
          </tbody>
        </table>
        </div>
      </Section>
    </div>
  );
}

/** Sign-offs from the survey workbook's `approved_stage3` tab. */
type Stage3Approvals = {
  approvals: Array<{
    containerNo: string;
    inspectionId: string;
    approverName: string;
    approverEmail: string;
    approverStatus: string;
    approvedAt: string;
    comment: string;
    reportUrl: string;
    /** The TAT stage this approver owns — null when they own neither 3.1 nor 3.2. */
    stage: string | null;
  }>;
  stageMarks: Record<string, { byName?: string; at?: string; note?: string }>;
  rowCount: number;
  error?: string;
  stale?: boolean;
};

/**
 * Who signed the inspection off.
 *
 * The measurements and the approval live on two different tabs of the survey
 * workbook, so this sits under the survey table rather than inside it. Every
 * recorded sign-off is listed, including approvers who own neither 3.1 nor 3.2
 * — hiding them is what made the Blueprint panel look like nobody had approved
 * anything.
 */
function Stage3ApprovalsBlock({ data }: { data?: Stage3Approvals }) {
  if (!data || data.error) return null;
  if (!data.approvals.length) {
    return data.rowCount > 0 ? (
      <div className="border-t border-slate-200 px-4 py-3 text-[11.5px] text-slate-500">
        {data.rowCount} inspection {data.rowCount === 1 ? 'row' : 'rows'} on file, but none records an approver yet.
      </div>
    ) : null;
  }

  return (
    <div className="border-t border-slate-200 px-4 py-3">
      <div className="mb-2 flex items-baseline gap-2">
        <h4 className="text-[12px] font-bold text-slate-700">Inspection approvals</h4>
        <span className="text-[10.5px] text-slate-400">
          Stage 3.1 / 3.2 sign-offs, read from the survey sheet{data.stale ? ' · showing the last cached copy' : ''}
        </span>
      </div>
      <ul className="space-y-1.5">
        {data.approvals.map((a, idx) => {
          const ok = /^approved$/i.test(a.approverStatus);
          return (
            <li
              key={`${a.inspectionId}-${idx}`}
              className={
                'rounded-lg border px-2.5 py-1.5 text-[11.5px] ' +
                (ok ? 'border-emerald-200 bg-emerald-50/60' : 'border-rose-200 bg-rose-50/60')
              }
            >
              <span className={ok ? 'font-semibold text-emerald-800' : 'font-semibold text-rose-800'}>
                {ok ? '✓' : '✗'} {a.approverName}
              </span>
              {a.stage && (
                <span className="ml-1.5 rounded bg-slate-700 px-1 py-0.5 font-mono text-[9px] font-bold text-white">
                  {a.stage}
                </span>
              )}
              {a.approvedAt && <span className="ml-1.5 text-slate-500">{fmtDate(a.approvedAt)}</span>}
              {a.containerNo && <span className="ml-1.5 font-mono text-[10.5px] text-slate-600">{a.containerNo}</span>}
              {a.reportUrl && (
                <a href={a.reportUrl} target="_blank" rel="noreferrer" className="ml-1.5 text-blue-600 hover:underline">
                  report ↗
                </a>
              )}
              {a.comment && <div className="mt-0.5 text-slate-600">“{a.comment}”</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// 7. INSPECTION — External container inspection reports (reads Stage 3 sheet)
function InspectionTab({ indentId, approvals }: { indentId: string; approvals?: Stage3Approvals }) {
  const { data, isLoading, isError, error } = useQuery<{
    noContainers: boolean;
    headers: string[];
    records: Record<string, any>[];
    error?: string;
  }>({
    queryKey: ['procurement', 'indent', indentId, 'inspections'],
    queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}/inspections`),
    staleTime: 60_000,
  });

  // Columns are whatever the Stage 3 sheet gives us — build them from its headers.
  const columns: Array<Column<Record<string, any>>> = (data?.headers || []).map((h) => ({
    key: h,
    header: h,
    value: (row) => safeVal(row[h]) ?? '',
    render: (row) => safeVal(row[h]) || '—',
  }));

  return (
    <Section title="What the surveyors found" hint="External container inspection reports (Stage 3 sheet)" flush>
      {isLoading && (
        <div style={{ padding: 24 }} className="text-center pc-text-muted">
          <span className="inline-block w-5 h-5 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          <p style={{ marginTop: 8 }}>Fetching inspection records…</p>
        </div>
      )}

      {!isLoading && (isError || data?.error) && (
        <div style={{ padding: 24 }} className="text-center">
          <Fa name="exclamation-triangle" size={22} className="pc-text-danger" />
          <p className="pc-text-danger" style={{ marginTop: 8 }}>{data?.error || String((error as any)?.message || 'Failed to read Stage 3 sheet')}</p>
        </div>
      )}

      {!isLoading && !isError && !data?.error && data?.noContainers && (
        <div style={{ padding: 24 }} className="text-center pc-text-muted">
          <Fa name="exclamation-triangle" size={22} className="pc-text-warning" />
          <p style={{ marginTop: 8 }}>No container numbers are currently mapped to this Indent.</p>
        </div>
      )}

      {!isLoading && !isError && !data?.error && !data?.noContainers && (data?.records?.length ?? 0) === 0 && (
        <div style={{ padding: 24 }} className="text-center pc-text-muted">
          <p style={{ padding: '2rem 0' }}>No inspection records have been filed for this indent's containers yet.</p>
        </div>
      )}

      {!isLoading && !!data?.records?.length && (
        <DataTable<Record<string, any>>
          rows={data.records}
          columns={columns}
          rowKey={(_row, idx) => String(idx)}
          exportName={`inspections-${indentId}`}
          searchPlaceholder="Search inspection records…"
          emptyText="No inspection records have been filed for this indent's containers yet."
        />
      )}

      {/* Rendered outside every branch above on purpose: the survey rows are
          matched by container + product code while approvals join by enquiry
          number, so an indent can have sign-offs on file even when the table
          above finds nothing to show. */}
      <Stage3ApprovalsBlock data={approvals} />
    </Section>
  );
}

// 8. GATE-IN — Depot Arrival Checklist (per-container gate-in status)
function GateInTab({ containers, indentId }: { containers: any[]; indentId: string }) {
  // Real containers only (skip WIP/placeholder rows). ProcurementContainer
  // fields: containerNo / currentStatus / gateInDate.
  const real = (containers || []).filter(
    (c) => !/\[WIP\]|^UNASSIGNED/i.test(String(c.containerNo || c.containerNumber || '')),
  );
  const openPortal = async () => {
    try {
      const r = await api.get<{ url: string }>(`/api/procurement/logistics/${encodeURIComponent(indentId)}/gate-in-url`);
      if (r.url) window.open(r.url, '_blank', 'noopener');
      else toast.error('Gate-In portal not configured');
    } catch (e: any) { toast.error(e?.message || 'Could not open gate-in portal'); }
  };
  const badge = (st: string) => {
    const s = String(st || '').toLowerCase();
    if (/in stock|verified/.test(s)) return { text: 'Gated In', cls: 'pc-bg-success', icon: 'check-circle' };
    if (/issue|damage/.test(s)) return { text: 'Issue Reported', cls: 'pc-bg-danger', icon: 'exclamation-triangle' };
    return { text: 'Pending Gate-In', cls: 'pc-bg-warning', icon: 'hourglass-half' };
  };
  return (
    <Section
      title="Has each container landed?"
      hint="Depot arrival checklist — one row per serial we are expecting"
      right={
        <button className="btn press inline-flex bg-teal-600 px-3 py-1.5 text-xs text-white hover:bg-teal-700" onClick={openPortal}>
          <Fa name="up-right-from-square" size={11} /> Open Portal
        </button>
      }
    >
      <div>
        {real.length === 0 ? (
          <div style={{ padding: 24 }} className="text-center pc-text-muted">
            No container serials attached yet. Use <b>Attach Serials</b> to add them as they arrive.
          </div>
        ) : (
          real.map((c, idx) => {
            const cn = c.containerNo || c.containerNumber || '—';
            const b = badge(c.currentStatus || c.status);
            return (
              <div key={c._id || cn || idx} className="flex items-center justify-between" style={{ padding: '11px 10px', borderBottom: idx < real.length - 1 ? '1px solid #F2F4F8' : 'none' }}>
                <span style={{ fontWeight: 700 }} className="font-mono pc-text-dark inline-flex items-center gap-2">
                  <Fa name={b.icon} size={12} className={b.cls === 'pc-bg-success' ? 'pc-text-success' : b.cls === 'pc-bg-danger' ? 'pc-text-danger' : 'pc-text-warning'} /> {cn}
                </span>
                <span className={`pc-badge ${b.cls} pc-shadow-sm`}>{b.text}</span>
              </div>
            );
          })
        )}
      </div>
    </Section>
  );
}

/* ════════════════════════ Primitives ════════════════════════ */

// NOTE: called as a plain function — `Safe(i.someField)` — so it takes the
// value directly. Must tolerate undefined/null (many indent fields don't
// exist on every doc); destructuring props here would throw on undefined and
// blank the whole tab. `safeVal` already null-guards.
function Safe(value?: any): React.ReactElement {
  const v = safeVal(value);
  return v ? <>{v}</> : <span className="pc-text-muted" style={{ fontStyle: 'italic' }}>N/A</span>;
}

function Badge(val: any, color: string): React.ReactNode {
  const v = safeVal(val);
  if (!v) return <span className="pc-text-muted" style={{ fontStyle: 'italic' }}>N/A</span>;
  return <span className={`pc-badge pc-bg-${color}`}>{v}</span>;
}

function TimelineStep({ cls, icon, label }: { cls: string; icon: string; label: string }) {
  return (
    <div className={`timeline-step ${cls}`}>
      <div className="timeline-icon"><Fa name={icon} size={18} /></div>
      <div className="timeline-label">{label}</div>
    </div>
  );
}

function Pill({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: string; label: string }) {
  return (
    <button type="button" className={`tower-pill ${active ? 'active' : ''}`} onClick={onClick}>
      <Fa name={icon} size={13} /> {label}
    </button>
  );
}

function CtaBtn({ color, icon, label, onClick, disabled }: { color: string; icon: string; label: string; onClick?: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={`pc-btn pc-btn-sm pc-btn-${color} pc-shadow-sm`} style={{ fontWeight: 700 }} onClick={onClick} disabled={disabled}>
      <Fa name={icon} size={11} className={icon === 'spinner' ? 'animate-spin' : ''} /> {label}
    </button>
  );
}

function VaultGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <p className="pc-text-muted" style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 8px' }}>{title}</p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16, borderBottom: '1px solid #E4E7EE', paddingBottom: 16 }}>{children}</div>
    </>
  );
}

// Reference `buildVaultBtn`: a button GROUP = [status/view button] + [blue "+"
// upload button]. The "+" is ALWAYS present (upload when pending, replace when
// present). Present → main button opens the doc; pending → red "… Pending".
function VaultBtn({ url, label, icon, slot, indentId, href }: {
  url: any; label: string; icon: string; slot?: string; indentId?: string;
  /** Direct link for a document that lives only in the portal sheet (uploaded
   *  through the old Apps Script portal, so there is no fileId to resolve). */
  href?: string;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const onFile = async (f: File) => {
    if (!slot || !indentId) return;
    setBusy(true);
    try {
      const up = await api.upload<{ id: string }>('/api/files/upload', f);
      await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/vault-doc`, { slot, fileId: up.id });
      toast.success(`${label} ${url ? 'replaced' : 'uploaded'}`);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
    } catch (e: any) { toast.error(e?.message || `Could not upload ${label}`); } finally { setBusy(false); }
  };
  const groupStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'stretch', marginBottom: 8 };
  const leftStyle: React.CSSProperties = { borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 0 };
  const plusStyle: React.CSSProperties = { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '0 8px', cursor: busy ? 'wait' : 'pointer' };
  const main = url ? (
    <a
      href={fileUrl(url)}
      onClick={(e) => { e.preventDefault(); openFileInNewTab(String(url)).catch(() => toast.error(`Could not open ${label}`)); }}
      target="_blank" rel="noreferrer"
      className="pc-btn pc-btn-sm pc-btn-outline-primary pc-shadow-sm" style={leftStyle}
    >
      <Fa name={icon} size={11} /> {label}
    </a>
  ) : href ? (
    // Filed on the sheet rather than uploaded through the app — open it
    // directly, and say so, so nobody re-uploads a document we already hold.
    <a
      href={href}
      target="_blank" rel="noreferrer"
      className="pc-btn pc-btn-sm pc-btn-outline-success pc-shadow-sm" style={leftStyle}
      title={`${label} — filed on the portal sheet`}
    >
      <Fa name={icon} size={11} /> {label}
      <span className="pc-badge pc-bg-success" style={{ marginLeft: 6, fontSize: 9 }}>sheet</span>
    </a>
  ) : (
    <span className="pc-btn pc-btn-sm pc-btn-outline-danger pc-shadow-sm" style={{ ...leftStyle, cursor: 'default' }}>
      <Fa name="lock" size={11} /> {label} Pending
    </span>
  );
  // No slot (e.g. Purchase Order which is set elsewhere) → status button only.
  if (!slot || !indentId) return <div style={groupStyle}>{main}</div>;
  return (
    <div style={groupStyle}>
      {main}
      <label className="pc-btn pc-btn-sm pc-btn-primary pc-shadow-sm" style={plusStyle} title={`Upload / replace ${label}`}>
        <Fa name={busy ? 'spinner' : 'plus'} size={11} />
        <input type="file" hidden disabled={busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.currentTarget.value = ''; }} />
      </label>
    </div>
  );
}

/* ──────────────────────────── Negotiate modal ──────────────────────────── */

function NegotiateModal({
  indentId, item, onClose, onDone,
}: { indentId: string; item: any; onClose: () => void; onDone: () => void }) {
  const [negotiatedRate, setNegotiatedRate] = useState<string>(String(item.rate || ''));
  const [remarks, setRemarks] = useState('');
  // Phase 3b — AI counter-offer draft. Advisory: prefills rate + remarks; the
  // purchaser edits and saves through the existing /negotiate endpoint.
  const aiOn = useAiEnabled();
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState<string>('');
  const draftAi = async () => {
    setAiBusy(true); setAiNote('');
    try {
      const r = await aiNegotiateDraft({
        indentId, itemId: item.itemId, vendorName: item.vendorName,
        currentRate: Number(item.rate) || 0, currency: item.currency || 'INR',
      });
      if (r.suggestion) {
        setNegotiatedRate(String(r.suggestion.suggestedRate || ''));
        setRemarks(r.suggestion.justification || '');
        setAiNote(`AI suggests ${item.currency} ${Number(r.suggestion.suggestedRate).toLocaleString('en-IN')} (−${r.suggestion.targetPct}%)${r.context?.lowestRivalRate ? ` · best rival ${item.currency} ${Number(r.context.lowestRivalRate).toLocaleString('en-IN')}` : ''}`);
      } else {
        setAiNote('AI could not draft a counter-offer — enter manually.');
      }
    } catch { setAiNote('AI draft failed — enter manually.'); }
    finally { setAiBusy(false); }
  };

  const submit = useMutation({
    mutationFn: () => api.post('/api/procurement/awards/negotiate', {
      indentId,
      itemId: item.itemId,
      vendorName: item.vendorName,
      originalRate: Number(item.rate) || 0,
      negotiatedRate: Number(negotiatedRate) || 0,
      currency: item.currency || 'INR',
      remarks: remarks.trim(),
    }),
    onSuccess: () => { onDone(); onClose(); },
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="pc-card pc-shadow-lg max-w-md w-full">
        <div className="pc-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Negotiate with {item.vendorName}</span>
          {aiOn && (
            <button onClick={draftAi} disabled={aiBusy} className="pc-btn pc-btn-sm pc-btn-outline-primary" title="Draft an AI counter-offer">
              {aiBusy ? '✨ Drafting…' : '✨ AI counter-offer'}
            </button>
          )}
        </div>
        <div className="pc-card-body">
          <p className="text-xs pc-text-secondary mb-4">Original rate <span className="font-mono font-bold">{item.currency} {Number(item.rate || 0).toLocaleString('en-IN')}</span></p>
          {aiNote && <div className="text-[11px] mb-3 px-2 py-1.5 rounded bg-blue-50 text-blue-800 border border-blue-100">{aiNote}</div>}
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider pc-text-secondary font-bold">New rate ({item.currency})</label>
              <input type="number" value={negotiatedRate} onChange={(e) => setNegotiatedRate(e.target.value)} className="pc-input mt-1 font-mono" placeholder="0" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider pc-text-secondary font-bold">Remarks</label>
              <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="pc-input mt-1" style={{ minHeight: 60 }} placeholder="Counter-offer notes…" />
            </div>
            {submit.isError && <div className="text-xs pc-text-danger">{String((submit.error as any)?.message || 'Failed')}</div>}
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onClose} className="pc-btn pc-btn-sm pc-btn-outline-secondary">Cancel</button>
            <button onClick={() => submit.mutate()} disabled={submit.isPending || !negotiatedRate} className="pc-btn pc-btn-sm pc-btn-primary">{submit.isPending ? 'Saving…' : 'Save negotiation'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * Every action available at each TAT stage.
 *
 * The stage table is read-only by design — a stage ticks when the real flow
 * moves the indent's status. These are the shortcuts to those flows, so nobody
 * has to remember which tab or modal owns which step.
 *
 * Most stages have SEVERAL genuine actions (sourcing alone covers compare/award,
 * the live-quote feed, manual quote entry, negotiation history and the LOI), so
 * all of them are listed instead of picking one and hiding the rest. Grouping
 * follows STATUS_STAGE on the server, so an action appears against the stage
 * whose statuses it actually drives.
 */
function stageDeepLinks(
  stage: string,
  indentId: string,
  isImport: boolean,
  setOpenModal: (k: ModalKey) => void,
  setTab: (t: TabKey) => void,
  navigate: (to: string) => void,
  extra: { pingAgent: () => void; requestVerify: () => void },
): StageActionSpec[] {
  const modal = (label: string, k: ModalKey): StageActionSpec => ({ label, run: () => setOpenModal(k) });
  const page = (label: string, to: string): StageActionSpec => ({ label, run: () => navigate(to) });
  const act = (label: string, run: () => void): StageActionSpec => ({ label, run });
  // The stage table sits ABOVE the tab strip, so merely switching tabs looked
  // like nothing happened — the new content was off-screen below. Scroll to it.
  const tab = (label: string, t: TabKey): StageActionSpec => ({
    label,
    run: () => {
      setTab(t);
      setTimeout(() => document.getElementById('tower-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    },
  });
  const id = encodeURIComponent(indentId);

  switch (String(stage || '').trim().toUpperCase()) {
    case 'STAGE 1':
      return [modal('Broadcast RFQ', 'rfq')];

    // Sourcing: award lives on the Split-Award Matrix PAGE (its own CTA only
    // shows at 'RFQ Completed', so this is the only way back to it later).
    case 'STAGE 2':
      // Watch what comes back, key in anything that arrived off-system,
      // haggle, then award and confirm it in writing.
      return [
        modal('Live quotes', 'liveQuotes'),
        page('Enter quotes', `/procurement/indents/${id}/quote`),
        modal('Negotiations', 'negotiations'),
        page('Compare & award', `/procurement/indents/${id}/compare`),
        modal('Issue LOI', 'loi'),
      ];

    /*
     * Import and Domestic genuinely do different work here, and the labels now
     * say which.
     *
     * Import closes 2.1 with a signed sales contract. Domestic does not sign
     * one at all — it emails the depot and surveyor the container details so
     * the inspection can be booked. Calling that "Upload Signed Contract" (or
     * even the neutral "Submit Stage 2.1") described the import path on a
     * domestic order, so the label now matches Operations' own wording for
     * the stage, which is what the TAT table and the sheet both call it.
     */
    case 'STAGE 2.1':
      return isImport
        ? [modal('Upload signed contract', 'contract')]
        : [modal('Send container details for inspection', 'stage21')];

    case 'STAGE 3':
      return [tab('Inspection', 'inspection'), modal('Attach serials', 'attach')];

    /*
     * These two sign-offs happen in the external survey workbook's approval
     * add-on — not on our Approvals desk, and not as a document anyone uploads
     * to the vault. Both of those buttons sent people somewhere the signature
     * could never be, so the only link here is the Inspection tab, which is
     * where the sheet's sign-offs are actually read and shown.
     */
    case 'STAGE 3.1':
    case 'STAGE 3.2':
      return [tab('Inspection sign-offs', 'inspection')];

    case 'STAGE 4':
      return [modal('Generate PO', 'po'), tab('Vault', 'vault')];

    // The proforma is what Accounts pay against, so it goes up first.
    case 'STAGE 5':
      return [modal('Upload PI', 'pi'), modal('Request FMS', 'payment'), tab('Ledger', 'finance')];

    case 'STAGE 6':
      return [modal('Set TAT clock', 'tat'), modal('Upload fulfilment docs', 'fulfillment')];

    // The broadest stage — it holds the whole move (and, on import, the entire
    // sea + customs leg), so it carries the most actions.
    case 'STAGE 7':
      return isImport
        ? [
            modal('Assign vessel', 'vessel'),
            modal('Complete SIMS', 'payment'),
            modal('Dispatch to CHA', 'cha'),
            act('Ping agent', extra.pingAgent),
            act('Request verify', extra.requestVerify),
            tab('Tracker', 'transit'),
          ]
        : [
            modal('Logistics RFQ', 'logRfq'),
            modal('Dispatch', 'dispatch'),
            modal('Attach serials', 'attach'),
            act('Request verify', extra.requestVerify),
            tab('Tracker', 'transit'),
          ];

    case 'STAGE 8':
      return [tab('Gate-In', 'gatein'), act('Request verify', extra.requestVerify)];

    default:
      return [];
  }
}
